import { useQuery } from "@tanstack/react-query";
import type { SellerAuthSessionResponse } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";

type UseSessionQueryParams = {
  storeSlug: string;
};

export function useSessionQuery({ storeSlug }: UseSessionQueryParams) {
  const query = useQuery({
    queryKey: queryKeys.seller.session(storeSlug),
    queryFn: () =>
      apiJsonClient<SellerAuthSessionResponse>(`${endpoints.seller.auth.me}?includeFirebaseToken=true`, {
        cache: "no-store",
      }),
    enabled: storeSlug.length > 0,
    refetchInterval: 50 * 60 * 1000, // refresh before Firebase custom token expires (1h)
    refetchIntervalInBackground: false,
  });

  // If the query has no successful data yet (e.g. a stale 401 error from a
  // previous session) and is currently re-fetching (e.g. right after login),
  // treat it as loading so ProtectedLayout doesn't redirect to /login before
  // the fresh response arrives.
  const hasSuccessfulData = !!query.data;
  const isSettling = query.isLoading || (!hasSuccessfulData && query.isFetching);

  return {
    data: query.data,
    isLoading: isSettling,
    error: isSettling ? null : query.error,
    refetch: query.refetch,
  };
}
